import { useGetChartingRecallSettings } from "@src/appV2/Charting/api/useGetChartingRecallSettings";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { PlacementAccessControlStatus } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useIsInterviewAvailabilityMenuVisible } from "@src/appV2/InterviewSettings/useIsInterviewAvailabilityMenuVisible";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { evaluateWorkplaceToWorkplaceReferrals } from "@src/containers/workplaceReferAndEarn/utils";
import { useFacilityOnboardingFlag } from "@src/hooks/useFacilityOnboardingFlag";
import { type SessionType } from "@src/modules/interface";
import constate from "constate";
import { useFlags } from "launchdarkly-react-client-sdk";
import includes from "lodash/includes";
import { useSelector } from "react-redux";

import { hasAnyTrueValue } from "./utils";
import { useShowLiveMapView } from "@src/containers/app/hooks/showLiveMapView";
import { useShowContractTerms } from "@src/containers/app/hooks/showContractTerms";

export interface WorkplaceMenuPriviliges {
  workplaceOnboarding: boolean;
  dailyView: boolean;
  calendar: boolean;
  chat: boolean;
  professionalsSection?: { myProfessionals: boolean; browseProfessionals: boolean };
  permanentHiringSection?: {
    placementsLandingPage: boolean;
    applicationAndJobs: boolean;
    browseTalentPool: boolean;
    interviewAvailability: boolean;
  };
  charting: boolean;
  analytics: boolean;
  liveWorkerETA: boolean;
  managementSection?: {
    myAccount: boolean;
    teamMembers: boolean;
    settings: boolean;
    timeclockingSettings: boolean;
  };
  billingSection?: {
    upcomingCharges: boolean;
    invoiceHistory: boolean;
    contractTerms: boolean;
  };
  helpCenter: boolean;
  referAndEarn: boolean;
}

export interface WorkplacePriviligesProviderProps {
  isWorkplace: boolean;
}

/* eslint-disable complexity */
export function useGetWorkplaceMenuPriviliges(
  props: WorkplacePriviligesProviderProps
): WorkplaceMenuPriviliges {
  const { isWorkplace } = props;
  const session = useSelector((state: SessionType) => state.session);
  const { admin: isAdmin, adminProfile, user, profile } = session ?? {};
  const { access: roles, permissions } = user ?? {};

  const workplaceOnboarding = useFacilityOnboardingFlag({
    workplaceCreationDate: profile?.createdAt,
    roles: roles ?? [],
    isAdmin,
    isWorkplace,
  });

  const hasDefaultWorkplacePermissions =
    [
      FACILITY_ROLES.ADMIN,
      FACILITY_ROLES.SHIFT_MANAGEMENT,
      FACILITY_ROLES.DOCUMENTS,
      FACILITY_ROLES.INVOICES,
    ].some((role) => includes(roles, role)) || isAdmin;

  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const isHomeHealthHiddenMenuItemsEnabled = useCbhFlag(
    CbhFeatureFlag.HOME_HEALTH_MENU_ITEMS_HIDDEN,
    {
      defaultValue: true,
    }
  );

  const hideMenuItemsForHomeHealth =
    isHomeHealthEnabled && isHomeHealthHiddenMenuItemsEnabled && !isAdmin;

  const dailyView = hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  const calendar = hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  const chat =
    (isAdmin && adminProfile.roles?.includes(EMPLOYEE_ROLES.VIEW_WORKPLACE_CHAT)) ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.DOCUMENTS) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT);
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const myProfessionals = true;

  const hasPermissionToAccessWorkerDirectory =
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    includes(roles, FACILITY_ROLES.RECRUITMENT) ||
    isAdmin;

  const ldFlags = useFlags();
  const hideBrowserWorkers = ldFlags?.[CbhFeatureFlag.HIDE_BROWSER_WORKERS];

  const browseProfessionals =
    hasPermissionToAccessWorkerDirectory && !isHomeHealthEnabled && !hideBrowserWorkers;

  const professionalsSection = { myProfessionals, browseProfessionals };

  const { accessLevel: placementsAccessLevel } = useGetPlacementsAccessLevel();

  const placementsLandingPage = placementsAccessLevel === PlacementAccessControlStatus.LANDING_PAGE;

  const applicationAndJobs = true;

  const browseTalentPool = placementsAccessLevel !== PlacementAccessControlStatus.EDIT_FREE;

  const interviewAvailability = useIsInterviewAvailabilityMenuVisible();

  const hasPermissionToRecruit =
    includes(roles, FACILITY_ROLES.RECRUITMENT) ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    isAdmin;

  const permanentHiringSection = {
    placementsLandingPage,
    applicationAndJobs,
    browseTalentPool,
    interviewAvailability,
  };

  const { chartingRecallEnabled: charting } = useGetChartingRecallSettings({
    workplaceId: profile?.userId,
  });

  const analyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });

  const analytics =
    analyticsConfig.length > 0 && (isAdmin || includes(roles, FACILITY_ROLES.ADMIN));

  const shouldShowLiveMapView = useShowLiveMapView();

  const liveWorkerETA =
    shouldShowLiveMapView && hasDefaultWorkplacePermissions && !isHomeHealthEnabled;

  const myAccount = ldFlags?.[CbhFeatureFlag.MY_ACCOUNT_PAGE] && !isAdmin;

  const teamMembers =
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    includes(roles, FACILITY_ROLES.INVOICES) ||
    isAdmin;

  const settings = includes(roles, FACILITY_ROLES.ADMIN) || isAdmin;

  const timeclockingSettings = includes(roles, FACILITY_ROLES.ADMIN) && !settings;

  const managementSection = { myAccount, teamMembers, settings, timeclockingSettings };

  const hasBillingRights =
    isAdmin || includes(roles, FACILITY_ROLES.ADMIN) || includes(roles, FACILITY_ROLES.INVOICES);

  const upcomingCharges =
    hasBillingRights ||
    includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT);

  const invoiceHistory =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES);

  const shouldShowContractTerms = useShowContractTerms(false);

  const hasViewContractTermsRights =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS);

  const contractTerms =
    shouldShowContractTerms && hasViewContractTermsRights && !hideMenuItemsForHomeHealth;

  const billingSection = { upcomingCharges, invoiceHistory, contractTerms };

  const helpCenter = true;

  const referAndEarn = evaluateWorkplaceToWorkplaceReferrals(ldFlags, user, isWorkplace, isAdmin);

  return {
    workplaceOnboarding,
    dailyView,
    calendar,
    chat,

    professionalsSection:
      (user
        ? isAdmin ||
          includes(user.access, FACILITY_ROLES.ADMIN) ||
          includes(user.access, FACILITY_ROLES.INVOICES) ||
          includes(user.access, FACILITY_ROLES.SHIFT_MANAGEMENT)
        : false) && hasAnyTrueValue(professionalsSection)
        ? professionalsSection
        : undefined,

    permanentHiringSection:
      hasPermissionToRecruit && hasAnyTrueValue(permanentHiringSection)
        ? permanentHiringSection
        : undefined,

    charting,
    analytics,
    liveWorkerETA,

    managementSection: hasAnyTrueValue(managementSection) ? managementSection : undefined,

    billingSection: hasAnyTrueValue(billingSection) ? billingSection : undefined,

    helpCenter,
    referAndEarn,
  };
}
/* eslint-enable complexity */

export const [WorkplaceMenuPriviligesProvider, useWorkplaceMenuPriviligesProviderContext] =
  constate(useGetWorkplaceMenuPriviliges);
